import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Col, DatePicker, Divider, Form, Input, InputNumber, Modal, Radio, Row, Select, Spin, Switch, Tooltip, Typography, message, } from 'antd';
import { DeleteOutlined, PlusOutlined, EyeOutlined, SaveOutlined } from '@ant-design/icons';
import GYAxios from '../../../util/GYAxios';
import moment from 'moment';
import './UniversalInvoiceForm.css';

const buildJsonHierarchyFromDottedKeys = (json) => {
  const result = {};
  Object.entries(json).forEach(([key, value]) => {
    if(value === null || value === undefined || (Array.isArray(value) && value.length === 0)) return;
    if(key.includes(".")) {
      const keys = key.split(".");
      if(!(keys[0] in result)) {
        result[keys[0]] = {};
      }
      result[keys[0]][keys[1]] = value;
    }
  });
  return result;
}

const buildDottedKeysFromJsonHierarchy = (json) => {
  const result = {};
  Object.entries(json).forEach(([key, value]) => {
    Object.entries(value).forEach(([subKey, subValue]) => {
      result[`${key}.${subKey}`] = subValue;
    });
  });
  return result;
}

const buildPdfMakeTextDynamicFieldConf = () => ([
  {
    fieldName: 'text',
    label: 'Text',
    type: "text",
    rules: [{ required: true, whitespace: false }]
  },
  {
    fieldName: 'bold',
    label: 'Bold',
    type: "switch",
  },
  {
    fieldName: 'italic',
    label: 'Italic',
    type: "switch",
  },
  {
    fieldName: 'fontSize',
    label: 'Font Size',
    type: "number",
  },
])

const buildPdfMakeImageDynamicFieldConf = () => ([
  {
    fieldName: 'image',
    label: 'Image Base64 URL',
    type: "text",
    rules: [{ required: true, whitespace: true }]
  },
  {
    fieldName: 'width',
    label: 'Width',
    type: "number",
  },
  {
    fieldName: 'height',
    label: 'Height',
    type: "number",
  }
]);

const formItemsConf = [
  {
    fieldName: "invoiceData_override.items",
    label: "Price items",
    type: "dynamic",
    allowImgInput: false,
    dynamicFields: () => ([
      {
        fieldName: "description",
        label: "Line item description",
        type: "text",
        rules: [{ required: true, whitespace: true }]
      },
      {
        fieldName: "quantity",
        label: "Quantity",
        type: "number",
        rules: [{ required: true, type: 'number', min: 1 }]
      },
      {
        fieldName: "unit_cost",
        label: "Unit Price",
        type: "number",
        rules: [{ required: true, type: 'number' }]
      },
      {
        fieldName: "line_total",
        label: "Line Total",
        type: "number",
        rules: [{ required: false, type: 'number' }]
      },
    ])
  },
  {
    fieldName: "docDef_override.noteUnderPriceTableSection",
    label: "Note Under Price Table Section",
    type: "dynamic",
    allowImgInput: true,
    dynamicFields: (inputType) => inputType === 'img' ? buildPdfMakeImageDynamicFieldConf() : buildPdfMakeTextDynamicFieldConf(),
  },
  {
    fieldName: "docDef_override.entitySection",
    label: "Entity Section",
    type: "dynamic",
    allowImgInput: true,
    dynamicFields: (inputType) => inputType === 'img' ? buildPdfMakeImageDynamicFieldConf() : buildPdfMakeTextDynamicFieldConf(),
  },
  {
    fieldName: "docDef_override.billingAddressSection",
    label: "Billing Address Section",
    type: "dynamic",
    allowImgInput: true,
    dynamicFields: (inputType) => inputType === 'img' ? buildPdfMakeImageDynamicFieldConf() : buildPdfMakeTextDynamicFieldConf(),
  },
  {
    fieldName: "docDef_override.postalAddressSection",
    label: "Postal Address Section",
    type: "dynamic",
    allowImgInput: true,
    dynamicFields: (inputType) => inputType === 'img' ? buildPdfMakeImageDynamicFieldConf() : buildPdfMakeTextDynamicFieldConf(),
  },
  {
    fieldName: "docDef_override.electricBillSection",
    label: "Electric Bill Section",
    type: "dynamic",
    allowImgInput: true,
    dynamicFields: (inputType) => inputType === 'img' ? buildPdfMakeImageDynamicFieldConf() : buildPdfMakeTextDynamicFieldConf(),
  },
  {
    fieldName: "docDef_override.footerSection",
    label: "Footer Section",
    type: "dynamic",
    allowImgInput: true,
    dynamicFields: (inputType) => inputType === 'img' ? buildPdfMakeImageDynamicFieldConf() : buildPdfMakeTextDynamicFieldConf(),
  },
];

// keep dynamic field types like:
// {
//   "docDef_override.noteUnderPriceTableSection": [],
//   "docDef_override.entitySection": ["img", "text", "text"],
//   "docDef_override.billingAddressSection": ["text"],
//   "docDef_override.postalAddressSection": ["img", "img"],
//   "docDef_override.electricBillSection": [],
//   "docDef_override.footerSection": []
// }
const getEmptyDynamicFieldTypes = () => Object.fromEntries(formItemsConf.filter(conf => conf.type === 'dynamic' && conf.allowImgInput).map(conf => ([conf.fieldName, []])))
const dynamicFieldTypes = { current: getEmptyDynamicFieldTypes() };
// build dynamic field types from json data from DB
const buildDynamicFieldTypes = dottedKeysValues => {
  const newDynamicFieldTypes = getEmptyDynamicFieldTypes();
  formItemsConf.forEach(conf => {
    if (conf.type === 'dynamic' && conf.allowImgInput && dottedKeysValues[conf.fieldName]) {
      dottedKeysValues[conf.fieldName].forEach(item => {
        if('image' in item) {
          newDynamicFieldTypes[conf.fieldName].push('img');
        } else {
          newDynamicFieldTypes[conf.fieldName].push('text');
        }
      });
    }
  });
  dynamicFieldTypes.current = newDynamicFieldTypes;
}

const renderFormItem = (conf) => {
  const { key, fieldName, label, type, rules, dynamicFields, allowImgInput } = conf;
  switch (type) {
    case "text":
      return (
        <Form.Item key={key || fieldName} name={fieldName} label={label} rules={rules}>
          <Input />
        </Form.Item>
      );
    case "number":
      return (
        <Form.Item key={key || fieldName} name={fieldName} label={label} rules={rules}>
          <InputNumber style={{ width: '100%' }} />
        </Form.Item>
      );
    case "switch":
      return (
        <Form.Item key={key || fieldName} name={fieldName} label={label} valuePropName="checked">
          <Switch />
        </Form.Item>
      );
    case "dynamic":
      return (
        <div key={key || fieldName}>
          <Divider orientation="left">{label}</Divider>
            <Form.List name={fieldName}>
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }, fieldIdx) => (
										<div key={key} style={{ display: 'flex', width: '100%', gap: 8, marginBottom: 8, alignItems: 'baseline' }}>
											{dynamicFields(dynamicFieldTypes.current[fieldName]?.[fieldIdx]).map((df) => (
												<div key={df.fieldName} style={{ flex: '1 1 0', minWidth: 0 }}>
													{renderFormItem({ ...restField, ...df, fieldName: [name, df.fieldName] })}
												</div>
											))}
											<DeleteOutlined style={{ flexShrink: 0, alignSelf: 'center' }} onClick={() => { if(allowImgInput) { dynamicFieldTypes.current[fieldName].splice(fieldIdx, 1) } remove(name) }} />
										</div>
                  ))}
                  <Form.Item>
                    <Button type="dashed" onClick={() =>{ if(allowImgInput) {dynamicFieldTypes.current[fieldName].push('text');} add();}} block icon={<PlusOutlined />}>
                      Add text item
                    </Button>
                    {allowImgInput && <Button type="dashed" onClick={() =>{ dynamicFieldTypes.current[fieldName].push('img'); add();}} block icon={<PlusOutlined />}>
                      Add image item
                    </Button>}
                  </Form.Item>
                </>
              )}
            </Form.List>
        </div>
      );
    default:
      return null;
  }
};


export default function UniversalInvoiceForm({ selectedSite, open = false, onClose = () => {}, onSubmit = ()=>{} }) {
	const [form] = Form.useForm();
	const [submitting, setSubmitting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [templateLoadError, setTemplateLoadError] = useState('');
  const tableName = selectedSite?.table_name;
  const billingDate = selectedSite?.dateData ? moment(selectedSite.dateData).format('YYYY-MM-DD') : '';

  useEffect(() => {
    let active = true;
    form.resetFields();
    setTemplateLoadError('');
    setLoadingTemplate(false);

    if (!open) return;
    if (!tableName || !moment(billingDate, 'YYYY-MM-DD', true).isValid()) {
      setTemplateLoadError('Select an invoice with a valid billing date to load its template.');
      return;
    }

    setLoadingTemplate(true);
    const loadTemplate = async () => {
      try {
        const response = await GYAxios.get('/invoice/universal_template', {
          params: { table_name: tableName, dateData: billingDate },
        });
        if (!active) return;
        if (response.data.template) {
          const dottedKeyObj = buildDottedKeysFromJsonHierarchy(response.data.template); 
          form.setFieldsValue(dottedKeyObj);
          buildDynamicFieldTypes(dottedKeyObj)
        }
      } finally {
        if (active) setLoadingTemplate(false);
      }
    };
    loadTemplate();

    return () => { active = false; };
  }, [open, tableName, billingDate, form]);

  const handlePreview = async () => {
    if (previewing || submitting || loadingTemplate || templateLoadError) return;
    if (!selectedSite?.table_name || !selectedSite?.dateData || !moment(selectedSite.dateData).isValid()) {
      message.error('Select an invoice with a valid billing date to preview.');
      return;
    }
    // validate if form is filled correctly
    try {
      await form.validateFields();
    } catch (error) {
      message.error('Please fill all required fields correctly before previewing.');
      return;
    }

    const previewTab = window.open('', '_blank');
    if (!previewTab) {
      message.warning('Allow pop-ups in your browser to preview the invoice.');
      return;
    }

    setPreviewing(true);
    let pdfUrl = '';
    try {
      previewTab.opener = null;
      previewTab.document.title = 'Invoice preview';
      previewTab.document.body.textContent = 'Generating invoice preview...';
      let values = await form.validateFields();
      values = calculateLineTotal(values);
      const response = await GYAxios.post('/invoice/pdf/universal_preview', {
        table_name: selectedSite.table_name,
        dateData: moment(selectedSite.dateData).format('YYYY-MM-DD'),
        ...(buildJsonHierarchyFromDottedKeys(values)),
      }, { timeout: 60000 });

      if (response.data.error || !response.data.pdfBase64) {
        message.error(response.data.error || 'Unable to generate invoice preview. Please try again.');
        return;
      }
      if (previewTab.closed) return;

      const decoded = window.atob(response.data.pdfBase64);
      const bytes = Uint8Array.from(decoded, character => character.charCodeAt(0));
      pdfUrl = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      previewTab.location.replace(pdfUrl);
      window.setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
    } catch (error) {
      if (!previewTab.closed) previewTab.close();
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
      if (!(error && typeof error === 'object' && 'errorFields' in error)) {
        message.error('Unable to preview the invoice. Please try again.');
      }
    } finally {
      setPreviewing(false);
    }
  };

  const calculateLineTotal = (values) => {
    values = JSON.parse(JSON.stringify(values));
    // calculate line total if not set
    if(values['invoiceData_override.items']?.length) {
      values['invoiceData_override.items'].forEach((item) => {
        if(!item.line_total && Number.isFinite(item.quantity) && Number.isFinite(item.unit_cost)) {
          item.line_total = parseFloat(item.quantity) * parseFloat(item.unit_cost);
        }
        item.line_total = item.line_total || 0;
      });
    }
    return values;
  }

	const handleFinish = async (values) => {
    if (previewing || submitting || loadingTemplate || templateLoadError) return;
    if (!tableName || !moment(billingDate, 'YYYY-MM-DD', true).isValid()) {
      message.error('Select an invoice with a valid billing date to save.');
      return;
    }
		let invoice = {
			...values
		};

    invoice = calculateLineTotal(invoice);

		setSubmitting(true);
		try {
      const response = await GYAxios.put('/invoice/universal_template', {
        table_name: tableName,
        dateData: billingDate,
        invoiceData_override: {},
        docDef_override: {},
        ...buildJsonHierarchyFromDottedKeys(invoice),
      });
      if (response.data.error || response.data.success !== true) {
        throw new Error('Unable to save invoice template');
      }
      message.success('Invoice template saved.');
      try {
        if (onSubmit) {
          await onSubmit(invoice);
        }
      } catch (error) {
        console.error('[UniversalInvoiceForm:onSubmit]', error);
        message.warning('Template saved, but the page could not refresh.');
			}
		} catch (error) {
      message.error('Unable to save the invoice template. Please try again.');
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Modal
			title="Universal Invoice Editor"
			open={open}
			onCancel={() => {
				if (!submitting) {
					onClose();
				}
			}}
			footer={null}
			width={1000}
			bodyStyle={{ maxHeight: '75vh', overflowY: 'auto' }}
			closable={!submitting}
			maskClosable={!submitting}
			keyboard={!submitting}
      forceRender
    >
      {templateLoadError && <Alert type="error" showIcon message={templateLoadError} style={{ marginBottom: 16 }} />}
      <Spin spinning={loadingTemplate}>
			<Form
				form={form}
        disabled={loadingTemplate || !!templateLoadError || submitting}
				name="universalInvoice"
        className="universal-invoice-form"
				layout="vertical"
				requiredMark="optional"
				initialValues={{}}
				onFinish={handleFinish}
				scrollToFirstError
			>
        {formItemsConf.map(conf => renderFormItem(conf))}
        <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={submitting} disabled={previewing || loadingTemplate || !!templateLoadError}>
          Submit invoice
        </Button>
        <Button htmlType="button" icon={<EyeOutlined />} onClick={handlePreview} loading={previewing} disabled={submitting || loadingTemplate || !!templateLoadError}>
          Preview Invoice
        </Button>
			</Form>
      </Spin>
		</Modal>
	);
}
